// ModernPricingPage.js
import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
} from "react";
import { RippleButton } from "./RippleButton";
import { useNavigate } from "react-router-dom";
import { toPersianDigits } from "../../../helper/toPersianDigits";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "../../../config/api";
import Spinner from "../../../Components/Loader/Spinner";
import Simple from "../../../Components/InputFields/Simple";
import SVG from "../../../Components/SVG";

const EXPECTED_AMOUNT_URL = `${API_BASE_URL}payment-payout/initialized/expected-amount/`;
const REFRESH_URL = `${API_BASE_URL}auth/trader/jwt/refresh/`;
const DEFAULT_COUPON_MESSAGE = "هنوز کد تخفیفی اعمال نشده است.";

// --- EXPORTED Building Blocks --- //

export const PricingCard = ({
  planName,
  description,
  price,
  discountPrice,
  discountPercent,
  features,
  buttonText,
  isPopular = false,
  buttonVariant = "primary",
}) => {
  const navigate = useNavigate();
  const hasDiscount = Boolean(discountPrice);
  const discountLabel = discountPercent
    ? `${toPersianDigits(discountPercent)}٪`
    : "";
  const [payablePriceIrr, setPayablePriceIrr] = useState("");
  const [payablePrice, setPayablePrice] = useState("");
  const [isExpectedAmountLoading, setIsExpectedAmountLoading] = useState(true);
  const [isCouponLoading, setIsCouponLoading] = useState(false);
  const couponInFlightRef = useRef(false);

  const logout = useCallback(() => {
    localStorage.removeItem("access");
    localStorage.removeItem("authCredentials");
    localStorage.removeItem("refresh");
    localStorage.removeItem("role");
    localStorage.removeItem("email");
    navigate("/signin");
  }, [navigate]);

  const refreshAccessToken = useCallback(async () => {
    const refreshResponse = await fetch(REFRESH_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        refresh: localStorage.getItem("refresh"),
      }),
    });

    if (!refreshResponse.ok) {
      logout();
      return null;
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);
    return refreshData.access;
  }, [logout]);

  const fetchWithAuthRetry = useCallback(
    async (url, options = {}) => {
      const getRequestOptions = () => ({
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...(options.headers ?? {}),
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
      });

      let response = await fetch(url, getRequestOptions());

      if (response.status !== 401) {
        return response;
      }

      const nextAccessToken = await refreshAccessToken();
      if (!nextAccessToken) {
        return null;
      }

      return fetch(url, getRequestOptions());
    },
    [refreshAccessToken],
  );

  const fetchExpectedAmount = useCallback(async () => {
    setIsExpectedAmountLoading(true);
    try {
      const response = await fetchWithAuthRetry(EXPECTED_AMOUNT_URL, {
        method: "GET",
      });
      if (!response) {
        return;
      }

      if (!response.ok) {
        throw new Error(`https error! Status: ${response.status}`);
      }

      const data = await response.json();
      const expectedAmount =
        data?.expected_amount_usd ?? data?.data?.expected_amount_usd;
      const expectedAmountIrr =
        data?.expected_amount_irr ?? data?.data?.expected_amount_irr;

      if (expectedAmount !== undefined && expectedAmount !== null) {
        setPayablePrice(Number(expectedAmount).toFixed(2));
        setPayablePriceIrr(Number(expectedAmountIrr));
      }
    } catch (error) {
      console.error("Error fetching expected amount:", error);
    } finally {
      setIsExpectedAmountLoading(false);
    }
  }, [fetchWithAuthRetry]);

  useEffect(() => {
    fetchExpectedAmount();
  }, [fetchExpectedAmount]);

  const cardClasses = `
    w-full max-w-sm backdrop-blur-[14px] mx-auto rounded-2xl flex-1 min-w-fit p-3 sm:p-5 flex flex-col md:flex-row transition-all duration-300
    bg-gradient-to-br from-white/10 to-white/5 border border-white/10
    ${isPopular ? "relative " : ""}
  `;
  const [ruleAgree, setRuleAgree] = useState(false);
  const canContinue =
    ruleAgree &&
    Boolean(payablePrice) &&
    !isExpectedAmountLoading &&
    !isCouponLoading;
  const buttonClasses = `
  ${canContinue ? "bg-cyan-600 hocus:opacity-85 text-black" : "bg-white/10 text-white/50 !cursor-not-allowed"}
     w-full py-1 rounded-xl font-medium text-base transition mt-3
    ${
      isPopular
      // ? "bg-cyan-400 hover:bg-cyan-300 !text-black"
      // : "border bg-white/10 hover:bg-white/20 text-white border-white/20"
    }
  `;
  const [couponCode, setCouponCode] = useState("");
  const [couponMessage, setCouponMessage] = useState(DEFAULT_COUPON_MESSAGE);
  const [couponMessageStatus, setCouponMessageStatus] = useState("idle");
  const handlePay = async () => {
    if (!canContinue) {
      return;
    }
    navigate("/purchase/confirm/payment", {
      state: {
        plan: {
          planName,
          description,
          price,
          discountPrice,
          discountPercent,
          features,
          buttonText,
          isPopular,
          buttonVariant,
        },
        planName,
        payablePrice,
        payablePriceIrr,
      },
    });
  };

  const handleCoupon = async () => {
    if (!couponCode || couponInFlightRef.current) return;
    couponInFlightRef.current = true;
    setIsCouponLoading(true);
    try {
      let response = await fetch(
        `${API_BASE_URL}payment-payout/coupons/apply/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
          body: JSON.stringify({ name: couponCode }),
        },
      );

      // If access token is invalid/expired → try refresh
      if (response.status === 401) {
        const refreshResponse = await fetch(
          `${API_BASE_URL}auth/trader/jwt/refresh/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              refresh: localStorage.getItem("refresh"),
            }),
          },
        );

        if (!refreshResponse.ok) {
          // refresh failed → logout
          localStorage.removeItem("access");
          localStorage.removeItem("authCredentials");
          localStorage.removeItem("refresh");
          localStorage.removeItem("role");
          localStorage.removeItem("email");
          navigate("/signin");
          return; // ⬅️ IMPORTANT
        }

        const refreshData = await refreshResponse.json();
        localStorage.setItem("access", refreshData.access);

        // retry original request with new access token
        response = await fetch(`${API_BASE_URL}payment-payout/coupons/apply/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
          body: JSON.stringify({ name: couponCode }),
        });
      }

      // Now handle final response (either first or retried)
      if (!response.ok) {
        setCouponMessage("کد تخفیف نامعتبر است.");
        setCouponMessageStatus("error");
        return;
      }

      setCouponMessage(`کد تخفیف با موفقیت اعمال شد!`);
      setCouponMessageStatus("success");
      await fetchExpectedAmount();
    } catch (error) {
      console.error("Error fetching account data:", error);
    } finally {
      couponInFlightRef.current = false;
      setIsCouponLoading(false);
    }
  };

  return (
    <div className={cardClasses.trim()}>
      {hasDiscount && (
        <div className="hidden absolute -top-4 right-4 px-3 py-1 text-sm font-normal rounded-full bg-cyan-400 text-black">
          {discountLabel ? `${discountLabel} تخفیف` : "تخفیف"}
        </div>
      )}
      <div className="flex-1">
        <div className="pb-3">
          <p className="text-sm font-thin text-center w-full text-[#fafafa] pb-0">
            چالش انتخاب شده
          </p>
          <h2 className="text-[48px] text-center font-extralight tracking-[-0.03em] text-[#fafafa] ">
            {planName}
          </h2>
          <div className="w-full flex justify-center items-center">
            <div className="flex flex-col gap-0">
              <p className="text-lg font-medium text-cyan-400 mt-0 font-sans">
                {description}
              </p>
              <span className="text-[14px] text-cyan-400/70">
                سرمایه اولیه
              </span>
            </div>
          </div>
        </div>

        <div className="hidden card-divider w-full h-px my-3 bg-white/20" />

        <div className="w-full flex flex-col justify-center items-center">
          <div className="flex items-center justify-between gap-2 w-full">
            <span className="text-sm text-[#fafafa]/70">
              هزینه چالش
            </span>
            <span className="text-lg font-extralight text-[#fafafa]">
              ${price}
            </span>
          </div>

          {hasDiscount && (
            <>
              {/* <div className="card-divider w-full h-px my-3 bg-white/20" /> */}

              <div className="flex items-center justify-between gap-2 w-full mt-4">
                <span className="text-sm text-[#fafafa]/70">
                  هزینه پس از تخفیف
                  {discountLabel ? ` ( ${discountLabel} )` : ""}
                </span>
                <span className="text-lg font-extralight text-[#fafafa]">
                  ${discountPrice}
                </span>
              </div>
            </>
          )}
          {/* <div className=" card-divider w-full h-px my-3 bg-white/20" /> */}
          {!hasDiscount && (
            <>
              <div className="flex flex-col gap-1 w-full mt-4">
                <div className="flex items-center justify-between gap-2 w-full pt-0">
                  <Simple
                    placeholder="کد تخفیف دارید؟"
                    type="text"
                    id="coupon"
                    formValue={couponCode}
                    formOnChange={(e) => {
                      const nextCouponCode = e.target.value;
                      setCouponCode(nextCouponCode);
                      setCouponMessage(
                        nextCouponCode ? "" : DEFAULT_COUPON_MESSAGE,
                      );
                      setCouponMessageStatus("idle");
                    }}
                  />
                  <button
                    onClick={handleCoupon}
                    type="button"
                    disabled={isCouponLoading || !couponCode}
                    className={`min-w-14 h-full px-3 py-2.5 rounded-lg text-sm flex items-center justify-center ${
                      isCouponLoading || !couponCode
                        ? "bg-zi-inputbg-dark text-zi-text-dark/50 cursor-not-allowed"
                        : "bg-zi-inputbg-dark text-zi-text-dark cursor-pointer"
                    }`}
                  >
                    {isCouponLoading ? (
                      <Spinner size={20} color="white" stroke={2} />
                    ) : (
                      "اعمال"
                    )}
                  </button>
                </div>
                <span
                  className={`text-xs ${
                    couponMessageStatus === "error"
                      ? "text-amber-400"
                      : couponMessageStatus === "success"
                        ? "text-[#37F100]"
                        : "text-[#fafafa]/50"
                  }`}
                >
                  {couponMessage}
                </span>
              </div>
              <div className="card-divider w-full my-4 h-px bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.09)_20%,rgba(255,255,255,0.22)_50%,rgba(255,255,255,0.09)_80%,transparent)]" />
            </>
          )}

          <div className="w-full flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2 w-full">
              <span className="text-sm font-medium text-[#fafafa]/70">
                مبلغ قابل پرداخت (دلار)
              </span>
              <span className="text-lg font-normal text-[#fafafa]">
                {isExpectedAmountLoading ? (
                  <span className="inline-flex min-w-10 justify-end">
                    <Spinner size={20} color="#808080" stroke={3} />
                  </span>
                ) : payablePrice ? (
                  `$${payablePrice}`
                ) : (
                  "-"
                )}
              </span>
            </div>
            <div className="flex items-center justify-between gap-2 w-full">
              <span className="text-sm font-medium text-[#fafafa]/70">
                مبلغ قابل پرداخت (ریال)
              </span>
              <span className="text-lg font-normal text-[#fafafa]">
                {isExpectedAmountLoading ? (
                  <span className="inline-flex min-w-10 justify-end">
                    <Spinner size={20} color="#808080" stroke={3} />
                  </span>
                ) : payablePriceIrr ? (
                  `${payablePriceIrr.toLocaleString()} ریال`
                ) : (
                  "-"
                )}
              </span>
            </div>
          </div>
        </div>

        <div className="w-full flex justify-start items-center gap-3 pt-8">
          <button
            type="button"
            aria-label={
              ruleAgree ? "قوانین پذیرفته شده" : "قوانین پذیرفته نشده"
            }
            onClick={(e) => {
              e.stopPropagation();
              setRuleAgree((prev) => !prev);
            }}
            className={
              "inline-flex size-5 items-center justify-center rounded-md transition-all " +
              (ruleAgree
                ? "bg-cyan-600"
                : "bg-transparent ring-1 ring-inset ring-cyan-600/50")
            }
          >
            {ruleAgree && (
              <SVG name="checkMark" classname="size-2.5 stroke-black" />
            )}
            {/* {isSelected && <span className="text-white text-xs">✓</span>} */}
          </button>
          <span className="text-sm font-medium text-[#fafafa]/70 flex gap-1.5">
            <Link
              className="underline underline-offset-4 text-cyan-600"
              to="/rule/general"
            >
              قوانین کوژیتو
            </Link>
            <span>را خوانده و پذیرفته‌ام.</span>
          </span>
        </div>

        <RippleButton
          className={buttonClasses.trim()}
          onClick={handlePay}
          disabled={!canContinue}
          ruleAgree={ruleAgree}
        >
          تأیید و ادامه
        </RippleButton>
      </div>

      <div className="hidden card-divider w-full h-px my-3 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.09)_20%,rgba(255,255,255,0.22)_50%,rgba(255,255,255,0.09)_80%,transparent)] md:mb-0 md:mx-5 md:w-px md:h-auto md:self-stretch md:shrink-0 md:bg-[linear-gradient(180deg,transparent,rgba(255,255,255,0.09)_20%,rgba(255,255,255,0.22)_50%,rgba(255,255,255,0.09)_80%,transparent)]" />
    </div>
  );
};

export const ChallengeFactor = ({ plan }) => {
  return (
    <main className="relative w-full max-w-5xl min-h-fit flex flex-col items-center justify-center mx-auto">
      <div className="w-full">
        <div className="w-full flex justify-center">
          <div className="w-full">
            <PricingCard {...plan} />
          </div>
        </div>
      </div>
    </main>
  );
};
